import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { authApi } from '../api/auth';
import { setUnauthorizedHandler } from '../api/apiClient';
import { tenantStore, tokenStore } from '../api/tokens';
import type { AuthTokens, TenantSummary, UserProfile } from '../types/auth';
import { endedSession } from './returnUrl';

/** Result of a login attempt: either fully signed in, or a tenant must be chosen first. */
export type LoginOutcome =
  | { status: 'authenticated'; user: UserProfile }
  | { status: 'select'; tenants: TenantSummary[] };

interface AuthContextValue {
  isAuthenticated: boolean;
  isBootstrapping: boolean;
  user: UserProfile | null;
  /** All tenants the signed-in person belongs to (drives the switcher). */
  tenants: TenantSummary[];
  /** Slug of the tenant the current session is acting in. */
  currentTenantSlug: string | null;
  /** True between a multi-tenant login and the tenant being chosen. */
  pendingTenantSelection: boolean;
  login: (email: string, password: string) => Promise<LoginOutcome>;
  /** Completes a multi-tenant login by choosing a tenant. */
  selectTenant: (tenantId: string) => Promise<UserProfile>;
  /** Switches the signed-in user to another of their tenants (in the role last used there). */
  switchTenant: (tenantId: string) => Promise<UserProfile>;
  /**
   * The one role this session acts in (29.16) — the server puts only that role in the token, so
   * `user.roles` holds just it. Null for an account without any role.
   */
  activeRole: string | null;
  /** Switches to one of the person's roles, in this tenant or another of theirs (tenant + role in one step). */
  switchRole: (tenantId: string, role: string) => Promise<UserProfile>;
  acceptInvitation: (token: string, password: string | null, firstName: string, lastName: string) => Promise<UserProfile>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<UserProfile | null>;
  setUser: (user: UserProfile) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

interface Props {
  children: ReactNode;
}

export function AuthProvider({ children }: Props): JSX.Element {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<UserProfile | null>(null);
  const [tenants, setTenants] = useState<TenantSummary[]>([]);
  const [currentTenantSlug, setCurrentTenantSlug] = useState<string | null>(null);
  const [selectionToken, setSelectionToken] = useState<string | null>(null);
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  // The session-ending paths below run outside render; the ref lets them name whose session ended.
  const userIdRef = useRef<string | null>(null);
  useEffect(() => {
    userIdRef.current = user?.id ?? null;
  }, [user]);

  const loadSession = useCallback(async (): Promise<UserProfile | null> => {
    try {
      const profile = await authApi.me();
      let list: TenantSummary[] = [];
      try {
        list = await authApi.myTenants();
      } catch {
        list = [];
      }
      setUser(profile);
      setTenants(list);
      setCurrentTenantSlug(tenantStore.get());
      return profile;
    } catch {
      setUser(null);
      tokenStore.clear();
      return null;
    }
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      if (userIdRef.current) endedSession.remember({ userId: userIdRef.current, reason: 'expired' });
      setUser(null);
      setTenants([]);
      setCurrentTenantSlug(null);
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  useEffect(() => {
    const refresh = tokenStore.getRefreshToken();
    if (!refresh) {
      setIsBootstrapping(false);
      return;
    }
    void loadSession().finally(() => setIsBootstrapping(false));
  }, [loadSession]);

  /**
   * Applies a freshly-minted token pair and synchronises the active tenant slug so that the
   * `X-Tenant-Slug` header always matches the tenant the token is bound to (the backend rejects a
   * mismatch). `slug` is null for a plain single-tenant login, where we derive it from the one
   * membership returned by the server.
   */
  const handleTokens = useCallback(async (tokens: AuthTokens, slug: string | null): Promise<UserProfile> => {
    tokenStore.setTokens(tokens);
    tenantStore.set(slug);

    const profile = await authApi.me();
    let list: TenantSummary[] = [];
    try {
      list = await authApi.myTenants();
    } catch {
      list = [];
    }

    let current = slug;
    if (!current && list.length === 1) current = list[0].slug;
    if (!current) current = tenantStore.get();
    tenantStore.set(current);

    // The active tenant is now final. Query hashes are tenant-scoped (tenantScopedHashFn), so a
    // stale entry from another tenant can never be served — this clear() only frees the dead
    // entries' memory and remains as defense-in-depth.
    queryClient.clear();

    setUser(profile);
    setTenants(list);
    setCurrentTenantSlug(current);
    return profile;
  }, [queryClient]);

  const login = useCallback(
    async (email: string, password: string): Promise<LoginOutcome> => {
      const result = await authApi.login(email, password);
      if (result.requiresTenantSelection) {
        setSelectionToken(result.selectionToken);
        setTenants(result.tenants ?? []);
        return { status: 'select', tenants: result.tenants ?? [] };
      }
      const profile = await handleTokens(result.tokens as AuthTokens, null);
      return { status: 'authenticated', user: profile };
    },
    [handleTokens],
  );

  const selectTenant = useCallback(
    async (tenantId: string): Promise<UserProfile> => {
      if (!selectionToken) {
        throw new Error('No pending tenant selection.');
      }
      const slug = tenants.find((t) => t.tenantId === tenantId)?.slug ?? null;
      const tokens = await authApi.selectTenant(selectionToken, tenantId);
      const profile = await handleTokens(tokens, slug);
      setSelectionToken(null);
      return profile;
    },
    [handleTokens, selectionToken, tenants],
  );

  const switchTenant = useCallback(
    async (tenantId: string): Promise<UserProfile> => {
      const slug = tenants.find((t) => t.tenantId === tenantId)?.slug ?? null;
      const tokens = await authApi.switchTenant(tenantId);
      return handleTokens(tokens, slug);
    },
    [handleTokens, tenants],
  );

  const switchRole = useCallback(
    async (tenantId: string, role: string): Promise<UserProfile> => {
      const slug = tenants.find((t) => t.tenantId === tenantId)?.slug ?? null;
      const tokens = await authApi.switchRole(tenantId, role);
      return handleTokens(tokens, slug);
    },
    [handleTokens, tenants],
  );

  const acceptInvitation = useCallback(
    async (token: string, password: string | null, firstName: string, lastName: string): Promise<UserProfile> => {
      const tokens = await authApi.acceptInvitation(token, password, firstName, lastName);
      return handleTokens(tokens, null);
    },
    [handleTokens],
  );

  const logout = useCallback(async (): Promise<void> => {
    const refresh = tokenStore.getRefreshToken();
    if (refresh) {
      try {
        await authApi.logout(refresh);
      } catch {
        // best effort
      }
    }
    if (userIdRef.current) endedSession.remember({ userId: userIdRef.current, reason: 'logout' });
    tokenStore.clear();
    tenantStore.set(null);
    queryClient.clear();
    setUser(null);
    setTenants([]);
    setCurrentTenantSlug(null);
    setSelectionToken(null);
  }, [queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({
      isAuthenticated: user !== null,
      isBootstrapping,
      user,
      tenants,
      currentTenantSlug,
      pendingTenantSelection: selectionToken !== null,
      login,
      selectTenant,
      switchTenant,
      activeRole: user?.roles[0] ?? null,
      switchRole,
      acceptInvitation,
      logout,
      refreshUser: loadSession,
      setUser,
    }),
    [user, tenants, currentTenantSlug, selectionToken, isBootstrapping, login, selectTenant, switchTenant, switchRole, acceptInvitation, logout, loadSession],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
